<template>
  <v-card class="mb-4">
    <v-progress-linear v-if="['uploading', 'parsing', 'running'].includes(list.job_status)"
                       indeterminate
                       height="10">
    </v-progress-linear>
    <v-row class="my-0 px-4">
      <v-col cols="12"
             md="3">
        <h2 class="text-h7">
          {{ list.filename }}
        </h2>
        <div class="text-caption">
          <div>
            Added: {{ list.created_at | datetime }}
          </div>
          <div>
            User: {{ list.user?.name }}
          </div>
          <div>
            Job ID: {{ list.job_id }}
          </div>
          <div>
            Status: {{ list.job_status }}
          </div>
          <div>
            Total Records Found: {{ totalRecords ?? 'TBD' }}
          </div>
          <div>
            Billable Emails Found: {{ billable ?? 'TBD' }}
          </div>
          <div>
            Duplicate Emails Found: {{ duplicates ?? 'TBD' }}
          </div>
        </div>
      </v-col>
      <template v-if="list.job_status === 'waiting_analyzed'">
        <v-col cols="12"
               md="7">
          <div class="text-center">
            <h3 class="text-h7 text-uppercase">
              Your Free Analysis Results
            </h3>
            <div>
              Estimated Bounce Rate: {{ list.bounce_estimate }}%
            </div>
            <div v-if="list.bounce_estimate > 10">
              This list is very dirty and requires cleaning prior to mailing.
            </div>
            <div v-else-if="list.bounce_estimate > 2">
              This list should be cleaned prior to mailing.
            </div>
          </div>
        </v-col>
      </template>
      <template v-else>
        <v-col cols="12"
               md="4">
          <h3 class="text-h7 text-uppercase">
            Validation Results
          </h3>
          <table class="EmailListCard__ResultDataTable mb-2">
            <tbody>
            <tr>
              <td>
                <v-avatar :color="colors[0]"
                          size="16">
                </v-avatar>
              </td>
              <td>
                Valid
              </td>
              <td class="text-right">
                {{ list.result?.total?.valid }}
              </td>
              <td class="text-right">
                {{ validPercentage }}
              </td>
            </tr>
            <tr>
              <td>
                <v-avatar :color="colors[1]"
                          size="16">
                </v-avatar>
              </td>
              <td>
                Accept All (Unverifiable)
              </td>
              <td class="text-right">
                {{ list.result?.total?.catchall }}
              </td>
              <td class="text-right">
                {{ catchallPercentage }}
              </td>
            </tr>
            <tr>
              <td>
                <v-avatar :color="colors[2]"
                          size="16">
                </v-avatar>
              </td>
              <td>
                Unknown
              </td>
              <td class="text-right">
                {{ list.result?.total?.unknown }}
              </td>
              <td class="text-right">
                {{ unknownPercentage }}
              </td>
            </tr>
            <tr>
              <td>
                <v-avatar :color="colors[3]"
                          size="16">
                </v-avatar>
              </td>
              <td>
                Disposable
              </td>
              <td class="text-right">
                {{ list.result?.total?.disposable }}
              </td>
              <td class="text-right">
                {{ disposablePercentage }}
              </td>
            </tr>
            <tr>
              <td>
                <v-avatar :color="colors[4]"
                          size="16">
                </v-avatar>
              </td>
              <td>
                Invalid
              </td>
              <td class="text-right">
                {{ list.result?.total?.invalid }}
              </td>
              <td class="text-right">
                {{ invalidPercentage }}
              </td>
            </tr>
            </tbody>
          </table>

          <a href="https://help.mobilelocker.com/article/257-how-to-clean-an-email-list#Email-Verification-Status-codes-uQW-9"
             target="_blank"
             class="text-caption text-decoration-underline">
            Learn more about your result codes.
          </a>
        </v-col>
        <v-col cols="12"
               md="3"
               class="d-flex flex-column justify-end">
          <apexchart v-if="total"
                     type="donut"
                     width="100%"
                     height="100%"
                     :options="chartOptions"
                     :series="chartSeries">
          </apexchart>
        </v-col>
      </template>
      <v-col cols="12"
             md="2">
        <v-btn v-if="['queued'].includes(list.job_status)"
               @click.prevent="parse"
               :disabled="busy || isProcessing"
               block
               small
               class="mb-2">
          Parse
        </v-btn>

        <v-btn v-if="['queued', 'waiting', 'waiting_analyzed'].includes(list.job_status)"
               @click.prevent="start"
               :disabled="busy || isProcessing"
               color="primary"
               block
               small
               class="mb-2">
          Clean list
        </v-btn>

        <v-btn v-if="list.job_done === false"
               @click.prevent="checkStatus"
               :disabled="busy"
               block
               small
               class="mb-2">
          Check Status
        </v-btn>

        <v-btn v-if="list.job_complete"
               @click.prevent="download"
               :disabled="busy || isProcessing"
               block
               small
               color="primary"
               class="mb-2">
          Download
        </v-btn>

        <v-btn v-if="['parsing', 'running', 'uploading'].includes(list.job_status) === false" @click.prevent="deleteList"
               :disabled="busy || isProcessing"
               dark
               color="red lighten-2"
               block
               small>
          Delete list
        </v-btn>
      </v-col>
    </v-row>

    <v-dialog v-model="showResultCodesModal"
              max-width="90%">
      <ResultCodesCard></ResultCodesCard>
    </v-dialog>
  </v-card>
</template>
<style lang="scss" scoped>

table.EmailListCard__ResultDataTable {
  font-size: 0.75rem !important;
  width: 100%;
}

</style>
<script>
import {mapGetters} from 'vuex';
import neverbounceService from '../../../services/neverbounceService';
import DOMPurify from 'dompurify';
import ResultCodesCard from './ResultCodesCard.vue';


export default {
  components: {ResultCodesCard},
  props: {
    list: {
      type: Object,
      required: true,
    },
    creditInfo: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      busy: false,
      showResultCodesModal: false,
    }
  },
  computed: {
    ...mapGetters([
      'team',
      'user',
    ]),
    chartSeries() {
      return [
        this.valid,
        this.catchall,
        this.unknown,
        this.disposable,
        this.invalid,
      ]
    },
    colors() {
      return [
        '#34795a', // Valid
        '#8d377c', // Accept All (Unverifiable)
        '#97c0cc', // Unknown
        '#b36bff', // Disposable
        '#eb5454', // Invalid
      ];
    },
    chartOptions() {
      return {
        animations: {
          enabled: false,
        },
        dataLabels: {
          enabled: false,
          style: {
            colors: this.colors,
          },
        },
        fill: {
          colors: this.colors,
        },
        labels: [
          'Valid',
          'Accept All (Unverifiable)',
          'Unknown',
          'Disposable',
          'Invalid',
        ],
        legend: {
          show: false,
        },

      }
    },
    isProcessing() {
      return ['running', 'parsing', 'uploading'].includes(this.list.job_status);
    },
    total() {
      return this.list.result?.total;
    },
    billable() {
      return this.total?.billable;
    },
    totalRecords() {
      return this.total?.records;
    },
    duplicates() {
      return this.total?.duplicates;
    },
    valid() {
      return this.total?.valid;
    },
    catchall() {
      return this.total?.catchall;
    },
    unknown() {
      return this.total?.unknown;
    },
    disposable() {
      return this.total?.disposable;
    },
    invalid() {
      return this.total?.invalid;
    },
    totalCount() {
      if (this.total) {
        const total = this.total;
        return total.valid
          + total.catchall
          + total.unknown
          + total.disposable
          + total.invalid;
      }
      return null;
    },
    validPercentage() {
      if (this.totalCount) {
        return `${Math.floor(this.valid / this.totalCount * 100)}%`;
      }
      return null;
    },
    catchallPercentage() {
      if (this.totalCount) {
        return `${Math.floor(this.catchall / this.totalCount * 100)}%`;
      }
      return null;
    },
    unknownPercentage() {
      if (this.totalCount) {
        return `${Math.floor(this.unknown / this.totalCount * 100)}%`;
      }
      return null;
    },
    disposablePercentage() {
      if (this.totalCount) {
        return `${Math.floor(this.disposable / this.totalCount * 100)}%`;
      }
      return null;
    },
    invalidPercentage() {
      if (this.totalCount) {
        return `${Math.floor(this.invalid / this.totalCount * 100)}%`;
      }
      return null;
    }
  },
  methods: {
    async parse() {
      const result = await neverbounceService.parse(this.list.job_id);
      console.log(result);
    },
    async start() {
      const result = await neverbounceService.start(this.list.job_id);
      console.log(result);
    },
    async checkStatus() {
      if (this.busy) {
        return;
      }
      try {
        this.busy = true;
        const list = await neverbounceService.checkStatus(this.list.id)
        this.$emit('updated', list);
      } catch (e) {
        this.$toast.error(DOMPurify.sanitize(e.message));
      } finally {
        this.busy = false;
      }
    },
    async deleteList() {
      if (confirm('Are you sure you want to delete this list?')) {
        await neverbounceService.deleteList(this.list.id)
        this.$toast.success('List deleted.');
        this.$emit('updated');
      }
    },
    async download() {
      await neverbounceService.downloadList(this.list);
    }
  }
}
</script>
